<template>
    <div
        v-show="!manualInput"
        class="question-row question-row-input"
        :data-question-to="props.content.next_question?.value"
        :data-linked-form-input="props.content.linked_form_input?.value"
        :data-lead-service-name="name"
    >
        <transition name="floating-label">
            <p
                v-if="hasFloatingLabel && state === 'show'"
                class="input__floating-label"
            >
                {{ props.content.placeholder?.value }}
            </p>
        </transition>

        <zoe-atom-select-with-search
            ref="select-with-search"
            :options="options"
            :placeholder="props.content.placeholder.value"
            :name="name"
            :class="hasFloatingLabel && state === 'show' && 'select-with-search--floating-label'"
            autocomplete="off"
            @update-value="updateValue"
            @update-results="updateResults"
            @click="resetValidations"
            @focus="resetValidations"
        />
        <p
            class="manual-input-toggle tiny-text"
            @click="showManualInput"
        >
            {{ translations.enterManually }}
        </p>
    </div>

    <template
        v-for="[key, field] in Object.entries(allFields)"
        :key="key"
    >
        <div
            v-show="manualInput || showIncompleteField(key)"
            class="question-row question-row-input"
        >
            <transition name="floating-label">
                <p
                    v-if="hasFloatingLabel && getInput(key) !== ''"
                    class="input__floating-label"
                >
                    {{ props.content[`${field.apiKey}_placeholder`]?.value || field.placeholder }}
                </p>
            </transition>

            <zoe-atom-input
                :value="getInput(key)"
                :name="key"
                :placeholder="props.content[`${field.apiKey}_placeholder`]?.value || field.placeholder"
                :mask="field.mask"
                :valid="isValid(key)"
                :class="(hasFloatingLabel && getInput(key) !== '') && 'input--floating-label'"
                @init="initInput(key)"
                @set="value => setInput(key, value)"
            />
        </div>
    </template>

    <div
        class="question-row question-row-button"
        :class="buttonClasses"
    >
        <button
            class="question-button-next button"
            type="button"
            value="next"
            data-showstopper="none"
            :data-question-to="props.content.next_question.value"
            @click="check"
        >
            <zoe-atom-text
                :item="props.content.button_label.value"
            />
        </button>
    </div>
</template>

<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useStore } from 'vuex';
import { v4 as uuidv4 } from 'uuid';
import ZoeAtomText from '@atoms/text/v1.0.0/zoe-atom-text.vue';
import ZoeAtomInput from '@atoms/input/v1.0.0/zoe-atom-input.vue';
import ZoeAtomSelectWithSearch from '@atoms/select-with-search/zoe-atom-select-with-search.vue';
import { fields } from '../../../../../js/flows/default-components/sb-address/v1/sb-address-1.0.0';
import { formTranslation } from '../../../../../../../resources/frontend/js/mixins/translations';
import { questionProps, getQuestionName, addExtraData, storeExtraData, getExtraData, handleResetValidation } from '@mixins/quiz/v2.0.0/question';
import { setInvalid } from '@mixins/quiz/v2.0.0/invalid';
import { checkCompleted } from '@mixins/quiz/v2.0.0/checks';
import { getNextQuestion } from '@mixins/quiz/v2.0.0/get-next-question';
import { validate } from '../../../../../js/vue-flows/default-components/sb-validate/v2/validate';
import { addressFetch, googlePlacesFetch, saveAddressLocation } from '@mixins/component/address-autocomplete';
import isEven from '@mixins/utils/is-even';
import stripHtml from '@mixins/utils/strip-html';
import getCurrentStep from '@mixins/utils/get-current-step';

import loaderIcon from '../../../../../../../resources/images/loader.gif';
import locationIcon from '../../../../../../../resources/images/location.svg';

const store = useStore();
const props = defineProps(questionProps);
const name = getQuestionName(props);
const uuid = ref(props.content?._info?.uuid);
const validator = ref('notEmpty');
const loading = ref(false);
const state = ref('tooShort');
const manualInput = ref(false);
const translations = ref({
    loading: formTranslation('address_autocomplete_loading') || 'Loading...',
    tooShort: formTranslation('address_autocomplete_too_short') || 'Please enter at least 3 characters',
    noResults: formTranslation('autocomplete_no_results') || 'We\'re not able to find a match, try another search',
    backToAutocomplete: formTranslation('address_autocomplete_back_to_autocomplete') || 'Back to autocomplete',
    enterManually: formTranslation('address_autocomplete_enter_manually') || 'Enter address manually',
});
const retrievedAddresses = ref([]);
const allFields = ref(fields(store.state.page.countryCode));
const filled = ref(false);
const manualFields = ref([]);
const apiProvider = ref('');
const apiSessionToken = ref(uuidv4());
const addressFetchTimeout = ref(false);
const trackIncompleteFields = ref([]);

onBeforeMount(() => {
    addExtraData(name, {
        name,
        value: '',
        mask: '',
        validator: validator.value,
        uuid: uuid.value,
        question: stripHtml(props.content.text.value),
        sendData: props.content.send_to_lead_service?.value !== false,
    });
});

const manualInputValues = computed(() => {
    const inputs = {};

    for (const field of Object.keys(allFields.value)) {
        inputs[field] = store.getters['form/getFieldData']('value', field);
    }

    return inputs;
});

const options = computed(() => {
    const options = [
        {
            name: 'options',
            options: [],
        },
    ];

    if (state.value === 'tooShort') {
        options[0].options = [
            {
                value: translations.value.tooShort,
                key: '',
            },
        ];
    }

    if (state.value === 'loading') {
        options[0].options = [
            {
                icon: loaderIcon,
                value: translations.value.loading,
                key: '',
            },
        ];
    }

    if (state.value === 'noResults') {
        options[0].options = [
            {
                value: translations.value.noResults,
                key: '',
            },
            {
                value: translations.value.enterManually,
                key: 'manual',
                button: true,
            },
        ];
    }

    if (state.value === 'show') {
        options[0].options = retrievedAddresses.value.map((address, index) => {
            return {
                icon: locationIcon,
                key: index,
                value: address.name,
            };
        });

        options[0].options.push({
            value: translations.value.enterManually,
            key: 'manual',
            button: true,
        });
    }

    return options;
});

const getInput = computed(() => {
    return name => {
        return store.getters['form/getFieldData']('value', name);
    };
});

const isValid = computed(() => {
    return name => {
        return store.getters['form/getFieldData']('validate', name);
    };
});

const buttonClasses = computed(() => {
    const manualFields = isEven(Object.keys(manualInputValues.value).length);
    const incompleteFields = isEven((trackIncompleteFields.value.length + 1));

    return {
        'question-row--full-width': (manualInput.value && manualFields) || incompleteFields,
    };
});

const currentStep = computed(() => {
    return getCurrentStep();
});

const hasFloatingLabel = computed(() => {
    return store.getters['styling/getInputForPlacement'](currentStep.value.key, 'floatingLabel');
});

const initInput = name => {
    const field = allFields.value[name];

    store.commit('form/addField', {
        step: 0,
        name: name,
        data: {
            name: name,
            value: '',
            validator: field.validator ?? 'notEmpty',
            question: field.placeholder,
        },
    });
};

const setInput = (name, value) => {
    store.commit('form/setFieldData', {
        name,
        key: 'label',
        value,
    });

    store.commit('form/setFieldData', {
        name,
        key: 'value',
        value,
    });
};

const resetApiSessionToken = () => {
    apiSessionToken.value = uuidv4();
};

const updateResults = async (value, manualInput = false) => {
    if (!manualInput) {
        manualFields.value = [];
        filled.value = false;

        store.commit('form/setFieldData', {
            name,
            key: 'value',
            value: '',
        });

        handleResetValidation(name);

        for (const field of Object.keys(allFields.value)) {
            store.commit('form/setFieldData', {
                name: field,
                key: 'value',
                value: '',
            });

            handleResetValidation(field);

            store.commit('user/setLocationValue', { key: 'zipcode', value: '' });
            store.commit('user/setLocationValue', { key: 'latitude', value: '' });
            store.commit('user/setLocationValue', { key: 'longitude', value: '' });
        }
    }

    if (value.length < 3) {
        state.value = 'tooShort';
        return;
    }

    if (!addressFetchTimeout.value) {
        addressFetchTimeout.value = true;

        setTimeout(() => {
            addressFetchTimeout.value = false;
        }, 300);

        state.value = 'loading';
        const addressResponse = await addressFetch(value, apiSessionToken.value);

        if (addressResponse.results.length === 0) {
            state.value = 'noResults';
        } else {
            state.value = 'show';
            apiProvider.value = addressResponse.provider;
            retrievedAddresses.value = addressResponse.results;
        }
    }
};

const updateValue = async (key, value) => {
    loading.value = true;

    if (key === 'manual') {
        manualInput.value = true;
        storeExtraData(name, 'value', 'N/A');
        return;
    }

    let address;

    if (apiProvider.value === 'geoapify') {
        address = await retrievedAddresses.value[key];
    } else if (apiProvider.value === 'googleMaps') {
        address = await googlePlacesFetch(retrievedAddresses.value[key].place_id, apiSessionToken.value);

        resetApiSessionToken();
    }

    storeExtraData(name, 'value', value);

    if (!address) return;

    for (const [name, field] of Object.entries(allFields.value)) {
        store.commit('form/setFieldData', { name, key: 'value', value: address[field.apiKey] || '' });
        store.commit('form/setFieldData', { name, key: 'label', value: address[field.apiKey] || '' });

        document.querySelector(`#${name}`).dispatchEvent(new Event('blur'));

        manualFields.value = manualFields.value.filter(fieldName => fieldName !== name);
    }

    saveAddressLocation(address);

    filled.value = true;
    loading.value = false;
};

const resetValidations = () => {
    if (manualInput.value) return;

    for (const field of Object.keys(allFields.value)) {
        handleResetValidation(field);
    }
};

const showIncompleteField = key => {
    if (!filled.value) {
        trackIncompleteFields.value = [];
        return false;
    }

    if (manualFields.value.includes(key)) {
        if (!trackIncompleteFields.value.includes(key)) {
            trackIncompleteFields.value.push(key);
        }

        return true;
    }

    if (
        store.getters['form/getFieldData']('value', key) === ''
        || !validate('form', key)
    ) {
        manualFields.value.push(key);
        return true;
    }

    return false;
};

const manualFieldsValid = () => {
    let valid = true;

    const shownFields = manualInput.value ? Object.keys(allFields.value) : manualFields.value;

    for (const field of shownFields) {
        if (
            store.getters['form/getFieldData']('value', field) === ''
            || !validate('form', field)
        ) {
            setInvalid(document.querySelector(`#${field}`));
            valid = false;
        }
    }

    return valid;
};

const showManualInput = () => {
    manualInput.value = !manualInput.value;

    if (manualInput.value && store.getters['form/getFieldData']('value', name) === '') {
        store.commit('form/setFieldData', {
            name: name,
            key: 'value',
            value: 'N/A',
        });
    }
};

const check = event => {
    const buttons = document.querySelectorAll('.button');

    if (document.activeElement.classList.contains('button')) document.activeElement.blur();

    if (!filled.value && !manualInput.value) {
        setInvalid(document.querySelector('.question-row-input'));
        return;
    }

    if (!manualFieldsValid()) return;

    const answerValue = getExtraData('value', name);
    const linkedFormInputIdentifier = '';
    const worktypeId = '';
    const enquiryDashboardRedirectVersion = '';
    const nextQuestion = getNextQuestion(event.target.dataset.questionTo, store.state.quiz.currentQuestion);

    checkCompleted(name, buttons, props.preHook, nextQuestion, props.afterHook, answerValue, linkedFormInputIdentifier, worktypeId, enquiryDashboardRedirectVersion);
};
</script>

<style lang="scss">
@use 'zoe-quiz-address-autocomplete';
</style>
